class ApiService {
    constructor() {
        this.baseURL = process.env.VUE_APP_API_ENDPOINT
        console.log(`ApiService > constructor > Token = ${this.token}`)
    }

    get token() {
        return localStorage.getItem('authToken');
    }

    set token(token){
        localStorage.setItem('authToken', token)
    }

    // Generic request method
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}${endpoint}`

        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...(this.token && { 'Authorization': `Bearer ${this.token}` }),
                ...options.headers
            },
            ...options
        }

        try {
            const response = await fetch(url, config)

            if (!response.ok) {
                if (response.status === 401) {
                    this.handleUnauthorized()
                    throw new Error('Unauthorized')
                }
                const body = await response.json().catch(() => ({}))
                throw new Error(body.error || `HTTP ${response.status}: ${response.statusText}`)
            }

            return await response.json()
        } catch (error) {
            console.error('API Request failed:', error)
            throw error
        }
    }

    clearToken() {
        localStorage.removeItem('authToken')
        localStorage.removeItem('user')
    }

    handleUnauthorized() {
        this.clearToken()
        window.location.href = '/app/login'  // /app is the Vue base, router sees /login
    }


    //---------------------------Auth------------------------------------
    async updateProfile(userData) {
        return await this.request(`/auth/profile`, {
            method: 'PUT',
            body: JSON.stringify(userData)
        })
    }


    //---------------------------Profile------------------------------------
    async submitT1Answers(answers, gender) {
        return await this.request('/profile/t1', {
            method: 'POST',
            body: JSON.stringify({ answers, gender })
        })
    }

    async getT1Interpretation() {
        return await this.request('/profile/t1/interpretation')
    }

    async submitT2Answers(answers, birthYear) {
        return await this.request('/profile/t2', {
            method: 'POST',
            body: JSON.stringify({ answers, birthYear })
        })
    }

    async getT2Interpretation() {
        return await this.request('/profile/t2/interpretation')
    }

    async retryT1Interpretation() {
        return await this.request('/profile/t1/interpretation/retry', { method: 'POST' })
    }

    async retryT2Interpretation() {
        return await this.request('/profile/t2/interpretation/retry', { method: 'POST' })
    }

    //---------------------------Matches------------------------------------
    async getMatches() {
        return await this.request('/matches', {
            method: 'GET'
        })
    }

    async createMatch(data) {
        return await this.request('/matches', {
            method: 'POST',
            body: JSON.stringify(data)
        })
    }

    async getMatch(id) {
        return await this.request(`/matches/${id}`, {
            method: 'GET'
        })
    }

    async getMatchStatus(id) {
        return await this.request(`/matches/${id}/status`, {
            method: 'GET'
        })
    }

    async acceptMatch(id) {
        return await this.request(`/matches/${id}/accept`, {
            method: 'POST'
        })
    }

    async declineMatch(id) {
        return await this.request(`/matches/${id}/decline`, {
            method: 'POST'
        })
    }

    async deleteMatch(id) {
        return await this.request(`/matches/${id}`, {
            method: 'DELETE'
        })
    }

    // Claims a match invitation token for the currently logged-in user (CREATED → CLAIMED).
    // Used when the user is already authenticated and arrives from a WhatsApp invite link.
    //
    // @param token {string} - invitation token from the invite URL
    // @returns {{ success: true }}
    async claimToken(token) {
        return await this.request('/matches/claim-token', {
            method: 'POST',
            body: JSON.stringify({ token })
        })
    }

    async retryMatchReport(id) {
        return await this.request(`/matches/${id}/report/retry`, {
            method: 'POST'
        })
    }

    async unlockPremium() {
        return await this.request('/profile/premium-unlock', {
            method: 'POST'
        })
    }


    async saveMatchRating(id, rate, comment) {
        return await this.request(`/matches/${id}/rate`, {
            method: 'PUT',
            body: JSON.stringify({ rate, comment })
        })
    }


    async saveProfileRating(rating, comment) {
        return await this.request('/profile/rating', {
            method: 'POST',
            body: JSON.stringify({ rating, comment })
        })
    }
}


// Create and export a singleton instance
const apiService = new ApiService()
export default apiService
